import { sendFapiRequest } from './fapi';
import { errorFrom as fapiErrorFrom } from './fapiErrors';
import { createTimeInstance } from '@utils/time';
import { logNetRequest } from '@utils/usefulFuncs';

// POST /feed/view — the ephemeral, human-facing read of a feed, any kind. Nothing is persisted
// server-side; the response IS the data. A multi-input feed (one computed by combining others)
// returns rows with per-input markers; a single-input score feed returns the same core rows without
// markers, plus its score spec's payload columns. Only reachable through sendFapiRequest: this is
// a cross-origin call to fapi with a Bearer JWT, which sendApiRequest cannot make (see fapi.js).
//
// Differences from /feed/publish that this module preserves rather than smooths over:
//   - rows whose inputs are missing are KEPT, with a null score, so the gap is visible;
//   - company_ids is a drill-down filter unique to this endpoint (whole-corpus is not expressible);
//   - there is no manifest, artifact, or idempotency key to report.
//
// There is no truncation model here, and deliberately so: the server refuses an over-cap slice with
// a 422 rather than handing back a silently shortened list, so a 200 is always the whole answer to
// the question that was asked. The cap itself is env-tunable server-side, which is the other reason
// nothing client-side may claim to know it.
export const FEED_VIEW_PATH = '/feed/view';

export const INPUT_STATUSES = {
  PRESENT: 'present',
  STALE: 'stale',
  MISSING: 'missing',
};

// ---------------------------------------------------------------------------------------------
// Response normalization.
//
// The wire shape is now documented key-by-key, so this section reads the documented names and only
// keeps an alias where the one real capture (src/features/feedView/mockFeed.js, taken 2026-08-20)
// contradicts the doc: `knowable_at` instead of `utc_knowable_at`, and `symbol` alongside `ticker`.
// A live probe has never been run, so dropping either alias would be trading a working reader for
// a document. Every remaining guess lives here so confirming the schema stays a one-file edit.
// ---------------------------------------------------------------------------------------------

const firstDefined = (source, keys) => {
  if (!source) return undefined;
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
};

// The response is an object with a `rows` array — both the doc and the capture agree, so the older
// envelope guesses (a bare array, `feed_rows`, `results`, `data`, `items`) are gone rather than
// carried as insurance against a shape nothing has ever produced.
const rowsFrom = (data) => (Array.isArray(data?.rows) ? data.rows : []);

const asFiniteNumber = (value) => (typeof value === 'number' && isFinite(value) ? value : null);

// A score is a number or it is absent. A missing-input row carries no score, and coercing that to
// 0 would invent a signal — the whole reason this endpoint keeps such rows is to show the gap.
const normalizeScore = (value) => asFiniteNumber(value);

const normalizeStatus = (value) => {
  const status = typeof value === 'string' ? value.trim().toLowerCase() : null;
  return status || null;
};

// `inputs` is an object keyed by the member's alias, and that KEY is the member's identity — the
// capture carries no per-input feed id at all, so there is nothing else to key on. `label` is still
// read because a friendlier display name costs nothing if a server ever adds one.
const normalizeMember = (key, value) => ({
  key: String(key),
  feedId: String(key),
  label: firstDefined(value, ['label', 'display_name', 'name']) ?? null,
  status: normalizeStatus(firstDefined(value, ['status', 'marker', 'state'])),
  // The member's own score for this row. A status pill says whether an input arrived; the value
  // says what it contributed.
  value: asFiniteNumber(firstDefined(value, ['value', 'score'])),
  // The rule that decided this input's status. `occasion` is available only on an exact timestamp
  // hit, so a carried-forward value reads `stale`; `standing` is available on any prior value at
  // all. It is the DECLARED mode — the aligner reports it before broadcast detection runs — so a
  // broadcast input arrives here labelled `standing`, and no reader may treat the two as distinct.
  // Not enumerated against a fixed set: the only vocabulary anyone has seen is `occasion`, from the
  // one capture, and rejecting an unrecognised value would drop the field the moment one ships.
  mode: normalizeStatus(firstDefined(value, ['mode'])),
  // The instant the value was carried FROM, which for a fresh occasion hit equals the row's own
  // knowable-at and for a carried one is earlier. Null on a `missing` input — there is nothing to
  // carry — so a reader wanting an age has to fall back to the row's stamp rather than assume this.
  //
  // Kept as the raw wire string. The capture emits it NAIVE ('2025-10-31T06:01:04') while the row
  // stamps carry '+00:00', and this project's dayjs has no utc plugin (see format.js), so whoever
  // subtracts these has to parse it as UTC explicitly. Coercing here would only hide that.
  srcKnowableAt: firstDefined(value, ['src_knowable_at']) ?? null,
});

const normalizeMembers = (row) => {
  const raw = row?.inputs;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.keys(raw).map((key) => normalizeMember(key, raw[key]));
};

const MEMBERSHIP_PREFIX = 'is_member_of_';

// Core row fields, plus the composition fields a multi-input feed adds. Everything else a row
// carries is the score spec's declared payload (`extra_columns`: explanation, thinking, …), which
// only single-input feeds have — so it's collected by exclusion rather than by a fixed list, since
// SIBLING FEEDS OF ONE SPEC NO LONGER SHARE A COLUMN SET: since PR #456 a score feed's columns are
// the spec-level extra columns UNION that score's own per-score ones, so drilling from one score of
// a spec to another can legitimately gain or lose columns. Collecting by exclusion is what makes
// that a non-event here; a fixed list would have had to be per-feed.
//
// A name belongs here only if the wire actually sends it. `value`, `name`, `combined_score`,
// `as_of` and `utc_knowable_time` were all in this set as aliases for core fields the doc and the
// capture agree are spelled otherwise, and the code that read them is gone — so all they did was
// eat a payload column of the same name. A spec may declare `extra_columns: ["as_of"]`, and until
// now that column vanished from the payload AND hijacked the row's timestamp and React key.
//
// `knowable_at` and `symbol` stay: the one real capture spells them that way while the doc says
// `utc_knowable_at` and `ticker`, and no live probe has settled which server is out there.
const CORE_ROW_KEYS = new Set([
  'company_id',
  'companyId',
  'ticker',
  'symbol',
  'company_name',
  'utc_knowable_at',
  'knowable_at',
  'score',
  'sector',
  'market_cap',
  'marketCap',
  'inputs',
  'members_present',
  'members_expected',
  'members_missing',
]);

// True for the three spellings a JSON encoder plausibly produces for a boolean flag: `true`, `1`,
// and the string `"true"` in any casing. The looseness is deliberate, and it is bounded on purpose.
//
// Deliberate, because the flags are read straight off a columnar store, and a parquet/pandas round
// trip is entirely capable of handing back `1` where the schema said boolean. A strict `=== true`
// turned that into an Indices cell reading "None" — not a blank, not an "unknown", but a positive
// claim that the company is in no index at all, which is a false statement ABOUT THE COMPANY made on
// the strength of an encoding detail.
//
// Bounded to an enumerated set rather than to truthiness, because the non-member spellings are what
// must never slip through, and `!!value` gets one of them exactly backwards: the string `"false"` —
// what a server that stringifies its booleans sends for a company that is NOT a member — is truthy.
// `0`, `null` and `undefined` all mean not-a-member too and are all rejected here by omission.
const isMembershipTrue = (value) => {
  if (value === true || value === 1) return true;
  return typeof value === 'string' && value.trim().toLowerCase() === 'true';
};

// The is_member_of_<index> flags collapse to the memberships that are true. Every flag ships on
// every row, so one column each would add dozens; the index names are what a reader wants anyway.
const normalizeMemberships = (row) =>
  Object.keys(row || {})
    .filter((key) => key.startsWith(MEMBERSHIP_PREFIX) && isMembershipTrue(row[key]))
    .map((key) => key.slice(MEMBERSHIP_PREFIX.length));

const hasMembershipFlags = (row) => Object.keys(row || {}).some((key) => key.startsWith(MEMBERSHIP_PREFIX));

const normalizePayload = (row) => {
  const payload = {};
  Object.keys(row || {}).forEach((key) => {
    if (CORE_ROW_KEYS.has(key) || key.startsWith(MEMBERSHIP_PREFIX)) return;
    if (row[key] === null || row[key] === undefined) return;
    payload[key] = row[key];
  });
  return payload;
};

const normalizeRow = (row, index) => {
  const companyId = firstDefined(row, ['company_id', 'companyId']);
  const knowableAt = firstDefined(row, ['utc_knowable_at', 'knowable_at']);
  return {
    // Stable identity for React keys and for the drill-down target. A company can appear on many
    // rows (one per knowable-at), so neither field alone is unique.
    key: `${companyId ?? 'unknown'}-${knowableAt ?? index}-${index}`,
    companyId: companyId !== undefined ? companyId : null,
    // Resolved point-in-time as of this row's knowable-at, so the same company can wear different
    // symbols on different rows of one result. It is a label, never an identity — company_id is.
    symbol: firstDefined(row, ['symbol', 'ticker']) ?? null,
    // Only present when `company_name` was named in the request's `columns`; it is a universe
    // column, not part of the floor.
    companyName: firstDefined(row, ['company_name']) ?? null,
    score: normalizeScore(firstDefined(row, ['score'])),
    knowableAt: knowableAt ?? null,
    // Point-in-time enrichment, resolved as-of this row's knowable-at. Null is a real answer — the
    // master may not be able to answer as of that date, and a macro row has no company at all.
    sector: firstDefined(row, ['sector']) ?? null,
    marketCap: asFiniteNumber(firstDefined(row, ['market_cap', 'marketCap'])),
    memberships: normalizeMemberships(row),
    hasMemberships: hasMembershipFlags(row),
    // A single-input feed's reasoning columns; empty for a multi-input feed, which has no per-row
    // payload (drill into a member to reach it).
    payload: normalizePayload(row),
    members: normalizeMembers(row),
    // Per-row arity, stated by the server rather than counted off the markers. Multi-input feeds
    // only, and dropped by `slim` along with `inputs` — so absence has two causes and neither is
    // "this row is complete".
    //
    // Null rather than 0 when absent, because a score feed composes nothing: "0 of 0 inputs
    // present" is a claim about a feed that has no inputs to count, and a reader that can't tell
    // that from a genuinely empty row will print it. `membersExpected === null` is the test for
    // "this row does not answer the question", and it is the one every consumer should use.
    membersPresent: asFiniteNumber(firstDefined(row, ['members_present'])),
    membersExpected: asFiniteNumber(firstDefined(row, ['members_expected'])),
    // ALIASES — the same key space `inputs` is keyed by, so these join straight onto `inputs`
    // without a lookup table.
    //
    // ⚠️ It includes STALE aliases despite the name: the server lists every alias whose status is
    // not `present`, and `inputs[alias].status` is the only place the two are separable. Anything
    // rendering this list must not call its contents "missing" on its own authority — that is the
    // exact conflation the per-row copy on this page has already been caught making once.
    membersMissing: Array.isArray(row?.members_missing) ? row.members_missing.map(String) : [],
  };
};

// Union of input keys across rows, in first-seen order — the table's marker columns. Taken from
// every row rather than the first because a row with a missing input may omit it entirely.
export const deriveMemberColumns = (rows) => {
  const seen = new Map();
  (rows || []).forEach((row) => {
    row.members.forEach((member) => {
      if (!seen.has(member.key)) {
        seen.set(member.key, { key: member.key, feedId: member.feedId, label: member.label });
      }
    });
  });
  return Array.from(seen.values());
};

// `new Date(null).getTime()` is 0, not NaN, so a falsy stamp has to be rejected before the isNaN
// test — the same guard `tickerChanges` writes out in format.js. Same three lines as rowSort's local
// `timeValue`; neither file imports the other, and a shared module for one expression would be worse
// than the repetition.
const instantOf = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
};

// Each row's score at that company's PREVIOUS knowable occasion, and the change against it.
//
// /feed/view has no prior-score, delta or history field. The floor is knowable-at, ticker,
// company_id and score, and `columns` is an allowlist validated against the feed's own universe — so
// naming an invented `prev_score` is a 422, which viewFeed rescues by dropping `columns` entirely and
// the page then caches as a refusal for the rest of the session, taking sector, market cap and index
// membership with it on every later read. The previous score is therefore derived here, from the rows
// this very response returned, and from nothing else.
//
// FOUR RULES, each load-bearing:
//
//   1. The PREVIOUS OCCASION, not the previous SCORED one. The predecessor is chosen by time alone;
//      if it scored nothing, `prevScore` is null and the walk does not continue backwards looking for
//      a number. Reaching further would present a figure from an arbitrary distance — three hours or
//      three quarters, and nothing on the row says which — as "previous", the same invention
//      `normalizeScore` refuses when it declines to read an absent score as zero. It costs: a
//      multi-input feed's rows are the union grid of its members' occasions and a misaligned occasion
//      still returns a row with a null score, so an unscored predecessor is the NORM there and these
//      columns are largely empty by construction. That is the honest rendering of a union grid.
//
//   2. TWO FIELDS, because one null covers two different facts and they have to stay separable — the
//      rule `membersExpected` states for itself and `MARKER_DESCRIPTIONS` states for the markers.
//      `prevScoreKnowableAt !== null` with a null `prevScore` is "the earlier occasion exists and
//      scored nothing"; `prevScoreKnowableAt === null` is "this read holds no earlier occasion for
//      this company". Both render an em dash, so a reader cannot reconstruct the difference from the
//      score alone — which is exactly why it is stored rather than recomputed.
//
//   3. STRICTLY EARLIER, resolved over distinct INSTANTS rather than over adjacent rows. The endpoint
//      claims (company_id, knowable_at) is unique and nothing enforces it — normalizeRow appends the
//      array index to `row.key` for that reason — so two rows can share an instant. They are peers,
//      not a sequence: neither is the other's predecessor and both take the same one. Walking buckets
//      rather than neighbours is also what makes the answer survive a re-read, since rowSort records
//      that server order is stable only within one response.
//
//   4. IT RUNS BEFORE EVERY FILTER, because it is called from normalizeViewResponse while the row
//      filters run in the table. So "previous" means the previous occasion in the READ, and no
//      client-side narrowing can quietly redefine it — the rule `buildCompanyLabelIndex` states for
//      itself. The cost is intended and visible: narrowing the knowable-at filter to a single day
//      leaves a prev score pointing at a day no longer on screen, which is why the cell carries the
//      predecessor's own instant in its title rather than leaving the analyst to find it.
//
// A row needs a company AND a parseable instant to take part: without either it gets no predecessor
// and is never anyone's. A macro row has no company at all, and grouping the id-less rows together
// would invent a series across unrelated ones.
export const attachPreviousScores = (rows) => {
  const list = rows || [];

  // company -> instant -> the rows stamped at that instant, in the order the server sent them.
  const byCompany = new Map();
  list.forEach((row, index) => {
    const instant = instantOf(row?.knowableAt);
    if (instant === null) return;
    if (row?.companyId === null || row?.companyId === undefined) return;
    // Keyed by String so a response that spells one company's id as a number and another's as a
    // string still groups each with itself.
    const company = String(row.companyId);
    if (!byCompany.has(company)) byCompany.set(company, new Map());
    const instants = byCompany.get(company);
    if (!instants.has(instant)) instants.set(instant, []);
    instants.get(instant).push({ row, index });
  });

  // Resolved once per instant rather than once per row, which is what makes rule 3 true by
  // construction: every row of one instant is handed the same predecessor.
  const predecessorOf = new Map();
  byCompany.forEach((instants) => {
    const ordered = Array.from(instants.keys()).sort((a, b) => a - b);
    ordered.forEach((instant, position) => {
      if (position === 0) return;
      const earlier = instants.get(ordered[position - 1]);
      // The last row of the earlier instant, in server order. Arbitrary, deliberately deterministic,
      // and only ever reachable on a response that breaks the endpoint's own uniqueness claim.
      const predecessor = earlier[earlier.length - 1].row;
      instants.get(instant).forEach((entry) => predecessorOf.set(entry.index, predecessor));
    });
  });

  return list.map((row, index) => {
    const predecessor = predecessorOf.get(index) || null;
    const prevScore = predecessor ? asFiniteNumber(predecessor.score) : null;
    const score = asFiniteNumber(row?.score);
    return {
      ...row,
      prevScore,
      prevScoreKnowableAt: predecessor ? predecessor.knowableAt : null,
      // Both operands or nothing. A row missing either has no measured movement to report — which is
      // a different thing from a change of ZERO, a company that scored the same twice. That is a
      // fact, so it is kept as 0 and printed, on the same footing as formatMemberships answering
      // "None" rather than an em dash.
      scoreDelta: score === null || prevScore === null ? null : score - prevScore,
    };
  });
};

// Wire names of this feed's membership flags. When the server states its universe we take the names
// from there, because `available_columns` includes flags that no returned row happens to carry —
// and the `columns` request field is an allowlist over exactly those names, so a name we never
// learned is a column we can never ask for. Against a server that predates `available_columns`
// (which includes the only capture we have) we fall back to the union over the rows themselves.
const indexFieldsFrom = (availableColumns, rawRows) => {
  if (availableColumns) return availableColumns.filter((name) => name.startsWith(MEMBERSHIP_PREFIX));
  return Array.from(
    new Set(rawRows.flatMap((row) => Object.keys(row || {}).filter((key) => key.startsWith(MEMBERSHIP_PREFIX))))
  );
};

// Turns a raw /feed/view payload into the shape the UI reads. Exported so a captured fixture goes
// through identical normalization — a mock that skipped this would be testing nothing.
export const normalizeViewResponse = (
  data,
  { slim = false, requestedColumns = null, knowableFloor = null, feedId = null } = {}
) => {
  const rawRows = rowsFrom(data);
  const rows = attachPreviousScores(rawRows.map(normalizeRow));
  // This feed's column universe: the floor plus every PIT, company_name and payload column it can
  // emit. Null means the server never said, which is a different thing from an empty universe and
  // has to stay distinguishable — every consumer falls back to guessing when it is null.
  const availableColumns = Array.isArray(data?.available_columns) ? data.available_columns : null;
  const rowCount = asFiniteNumber(data?.row_count);
  return {
    status: 200,
    rows,
    // WHICH FEED THIS RESPONSE IS ABOUT. The server's echo first, the requested id second: if the
    // two ever disagree, the rows are what the server says they are, not what was asked for.
    //
    // Carried on the response because everything the page keys per feed — hidden columns, the column
    // universe, a cached column refusal — needs the pairing, and without it the page had to
    // reconstruct it out of band from a ref assigned during render. A response that does not know
    // its own feed is one a late arrival can file against the wrong one.
    feedId: data?.feed_id || feedId || null,
    // The server's own count. It equals rows.length by construction (nothing is truncated), so the
    // fallback is for a server that omits the field, not for a disagreement worth surfacing.
    rowCount: rowCount === null ? rows.length : rowCount,
    availableColumns,
    // What this response was ASKED for, which is a different question from what the feed could have
    // offered. `columns` is additive: a column in the universe that nobody named is simply absent
    // from these rows, and a reader that can't tell the two apart renders "None" for a membership
    // nobody looked up. Null means no `columns` was sent — including after a retry dropped them.
    requestedColumns: requestedColumns && requestedColumns.length ? requestedColumns : null,
    indexFields: indexFieldsFrom(availableColumns, rawRows),
    memberColumns: deriveMemberColumns(rows),
    // The lower bound this read actually sent, recorded rather than computed — it is the only thing
    // that separates "no earlier occasion exists" from "no earlier occasion was asked for". A null
    // floor is an UNBOUNDED read, which the default form state produces (fromDate starts null), and
    // over one of those a row with no predecessor genuinely has none in the feed. Over a bounded read
    // the same row proves nothing, and whoever words the cell must not claim otherwise.
    knowableFloor: knowableFloor || null,
    slim: !!slim,
    error: null,
  };
};

// ---------------------------------------------------------------------------------------------
// Errors. FastAPI reports its own failures under `detail` (a string, or a list of validation
// objects), so that is checked before the `error` key the Rails-backed services use.
// ---------------------------------------------------------------------------------------------

// A 422 has three documented causes and they share a status code: an unknown feed id, a column name
// outside this feed's universe, or a slice over the server's row cap. The server's own message is
// the only way to tell them apart, and errorFrom prefers it over the fallback below whenever it
// sends one — so this string only has to cover the case where it sends nothing at all.
//
// The test is which FIELD the server blamed, not whether the word "column" appears somewhere in the
// body. Searching the whole body meant the detail for an unknown feed id — which echoes the id back
// — accused the columns whenever the feed was named something like `column_screen_v1`, and that feed
// then paid two full ~24-second engine passes to be told the same thing twice.
const COLUMNS_FIELD = 'columns';

// A FastAPI validation error points at the rejected field, and `loc: ["body", "columns"]` is what an
// unknown column name and an `extra: forbid` rejection of the whole key both produce. Keying on
// `loc` survives any rewording of `msg`.
const locNamesColumns = (entry) =>
  Array.isArray(entry?.loc) && entry.loc.some((part) => String(part).toLowerCase() === COLUMNS_FIELD);

// A server that answers in prose instead of a validation list still has to name the field it is
// refusing. `\b` counts `_` as a word character, so `column_screen_v1` is not a match on its own;
// the feed id is cut out first so a hyphenated id like `column-screen-v1` isn't one either.
const COLUMNS_WORD = /\bcolumns?\b/;

const proseNamesColumns = (value, feedId) => {
  if (typeof value !== 'string') return false;
  const message = value.toLowerCase();
  const withoutFeedId = feedId ? message.split(String(feedId).toLowerCase()).join(' ') : message;
  return COLUMNS_WORD.test(withoutFeedId);
};

const blamesColumnsField = (data, feedId) => {
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    return detail.some((entry) => locNamesColumns(entry) || proseNamesColumns(entry?.msg, feedId));
  }
  if (detail && typeof detail === 'object') {
    return locNamesColumns(detail) || proseNamesColumns(detail.msg || detail.message, feedId);
  }
  return [detail, data?.error, data?.message].some((part) => proseNamesColumns(part, feedId));
};

// WHICH column the server refused, when it says so in a way that cannot be misread.
//
// A FastAPI validation error carries `loc`, the path to the value it rejected: `["body","columns",2]`
// points at the THIRD name in the list this very request sent. So the name is resolved out of our own
// request, and the server's wording never enters into it. A `loc` that stops at `columns` is blaming
// the whole key — what an `extra: forbid` rejection produces — and names no column, so it reports
// none, which is the honest answer.
//
// Nothing is read out of `msg`, and that is the whole design. Servers phrase this differently from
// release to release, and a sentence containing the word `sector` may be refusing the FEED
// `sector_rotation_v1` rather than any column. A notice naming the wrong column sends an analyst to
// remove a column that was never the problem, which is strictly worse than the generic wording the
// page already falls back to when this reports nothing.
const refusedColumnsFrom = (data, requestedColumns) => {
  const requested = Array.isArray(requestedColumns) ? requestedColumns : [];
  if (!requested.length) return [];
  const entries = Array.isArray(data?.detail) ? data.detail : [data?.detail];
  const names = [];
  entries.forEach((entry) => {
    if (!Array.isArray(entry?.loc)) return;
    const at = entry.loc.findIndex((part) => String(part).toLowerCase() === COLUMNS_FIELD);
    if (at === -1) return;
    const position = entry.loc[at + 1];
    // An index into the list we sent is the one thing that resolves to a name. A string is taken
    // only when it is a name we actually sent, so no `loc` shape — expected or not — can introduce a
    // column nobody asked for.
    const name =
      Number.isInteger(position) && position >= 0 && position < requested.length
        ? requested[position]
        : requested.find((column) => column === position);
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
};

const ERROR_FALLBACKS = {
  // Not "no feed with that id": GET /feeds fails open on a bad multi-input registry and omits
  // derived feeds entirely, so an id absent from the picker can still be a real, viewable feed.
  422: 'The server refused that query. Either the feed id isn’t one it serves, a requested column isn’t part of this feed, or the result was over the row cap — narrow the knowable window or name specific companies. The feed list can be incomplete, so a valid id may be missing from it.',
  503: 'The feed registry is still loading on the server — nothing is wrong with your query. Try again in a moment.',
  403: 'This account isn’t cleared for feed view. Ask a Tornado admin to add the feed_view grant, then reload this page.',
  401: 'Your session isn’t authorized for the feed API. Sign out and back in, then run the query again.',
};

const errorFrom = (data, error, status) => fapiErrorFrom(data, error, status, ERROR_FALLBACKS);

// ---------------------------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------------------------

// Date-only pickers give midnight-local Dates; widening to the day's edges keeps the day the
// analyst picked inside the window on both ends. Optional keys are omitted, never sent as null.
const toIsoStart = (date) => (date ? createTimeInstance(date).startOf('day').toISOString() : null);
const toIsoEnd = (date) => (date ? createTimeInstance(date).endOf('day').toISOString() : null);

export const buildViewBody = ({ feedId, knowableFrom, knowableTo, companyIds, slim = false, columns }) => {
  const body = { feed_id: String(feedId || '').trim(), slim: !!slim };
  // `columns` is ADDITIVE, not a filter: omitting it asks for the floor alone (knowable-at, ticker,
  // company_id, score), and naming a column adds it on top. So the list is sent whenever the caller
  // built one — an empty list would be indistinguishable from omitting it, hence the length check.
  if (Array.isArray(columns) && columns.length) body.columns = columns;
  const from = toIsoStart(knowableFrom);
  const to = toIsoEnd(knowableTo);
  if (from) body.knowable_from = from;
  if (to) body.knowable_to = to;
  // Number(null) and Number('') are both 0, which would silently ask for company 0 — screen the
  // empties out before coercing rather than after. The field is documented list<int>, so a float
  // like '456.7' is dropped rather than sent: an id the server would reject outright.
  const ids = (companyIds || [])
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id));
  if (ids.length) body.company_ids = ids;
  return body;
};

const requestView = async (body, signal) => {
  const response = await sendFapiRequest('post', FEED_VIEW_PATH, body, { signal });
  logNetRequest(FEED_VIEW_PATH, response.status, response.data, response.error);
  return response;
};

const resultFrom = ({ status, data, error, aborted }, body) => {
  // The caller cancelled — not an outcome to report, and not an error to render.
  if (aborted) {
    return {
      status,
      rows: null,
      aborted: true,
      // Named even here. A cancel that comes back anonymous leaves the caller unable to tell which
      // read it was that went away, which on a drill-down is the difference between rolling back to
      // the parent and leaving the page pointed at a feed it never loaded.
      feedId: body.feed_id ?? null,
      memberColumns: [],
      rowCount: 0,
      availableColumns: null,
      requestedColumns: body.columns ?? null,
      knowableFloor: body.knowable_from ?? null,
      error: null,
    };
  }

  if (status === 200 && data) {
    return normalizeViewResponse(data, {
      slim: body.slim,
      requestedColumns: body.columns,
      knowableFloor: body.knowable_from,
      feedId: body.feed_id,
    });
  }

  return {
    status,
    rows: null,
    // And here. The column-refusal cache keys on the feed id, so a 422 that does not say which feed
    // it was about files the refusal against whatever happened to be on screen.
    feedId: body.feed_id ?? null,
    memberColumns: [],
    rowCount: 0,
    availableColumns: null,
    requestedColumns: body.columns ?? null,
    knowableFloor: body.knowable_from ?? null,
    slim: !!body.slim,
    // 422 is caller-fixable (wrong feed, wrong column, too many rows); 503 is a cold process and
    // worth retrying unchanged.
    retryable: status === 503 || status === 0 || status >= 500,
    error: errorFrom(data, error, status),
  };
};

// One read of one feed. `params` is what buildViewBody consumes, plus an optional `signal`.
//
// A `speculativeColumns` flag used to ride along here, saying the `columns` list was a guess made
// before this feed's `available_columns` had ever been seen. It is gone. Once the retry below
// narrowed to gate on the SERVER naming the `columns` field, no branch of this function read the
// flag — and a parameter that nothing reads is a standing claim that it changes the behaviour,
// which the next reader has to disprove by grep before they can trust either side of the call.
export const viewFeed = async (params) => {
  const body = buildViewBody(params);
  const response = await requestView(body, params?.signal);

  // Retry ONCE, without `columns`, and only when the SERVER blamed the columns FIELD. A guess is
  // essentially every first read of a feed, so treating "we were guessing" as grounds to retry made
  // a typo'd feed id and an over-cap slice each pay two full ~24-second engine passes before saying
  // the same thing twice. Blaming the `columns` field is what a FastAPI `extra: forbid` rejection of
  // the key and an unknown column name both do, which is the case worth the second call. The feed id
  // is passed so the server echoing it back cannot make a feed accuse its own columns.
  const columnsMayBeTheCause =
    response.status === 422 && !!body.columns && blamesColumnsField(response.data, body.feed_id);

  if (columnsMayBeTheCause) {
    // Read the refused names off the FIRST refusal, before the retry replaces it. The retry sends no
    // `columns` at all, so nothing it says back can be about one.
    const refusedColumns = refusedColumnsFrom(response.data, body.columns);
    const floorBody = { ...body };
    delete floorBody.columns;
    const retry = await requestView(floorBody, params?.signal);
    // Cancelled mid-retry: the analyst walked away from this read, and reporting the 422 that
    // triggered the retry would blame the query for something the analyst chose to stop.
    if (retry.aborted) return resultFrom(retry, floorBody);
    if (retry.status === 200 && retry.data) {
      // The read succeeded, so this is a result and not an error — but the enrichment the analyst
      // asked for isn't in it, and the UI has to be able to say so. `requestedColumns` is null here
      // for the same reason: this response was asked for nothing beyond the floor.
      // `refusedColumns` is how the notice names the column instead of saying "the column request"
      // — empty when the server refused without naming one, which the page renders generically.
      return {
        ...normalizeViewResponse(retry.data, {
          slim: floorBody.slim,
          requestedColumns: null,
          // The retry dropped `columns`, not the window — the floor is still whatever was asked for.
          knowableFloor: floorBody.knowable_from,
          // Nor the feed. Without this the ONE response that carries droppedColumns comes back
          // anonymous whenever the server does not echo feed_id — and the refusal cache is keyed on
          // the feed, so the negative cache never gets written on the read that discovered the
          // refusal. The feed then pays the 422-then-retry double pass on every later read forever,
          // the "ask again" button hides itself, and the analyst's hidden columns file under the
          // literal key "null".
          feedId: floorBody.feed_id,
        }),
        droppedColumns: true,
        refusedColumns,
      };
    }
    // Dropping the columns did not rescue the read. The original refusal is still the one to report
    // — the second is about a request the caller never made — but the fact that the server blamed
    // the `columns` field is part of what happened and has to survive. Without it the caller sees an
    // undifferentiated 422 and cannot know that its column request was implicated at all, so it
    // cannot offer to clear it; the analyst is left rereading a wrong-feed-id message for the cause.
    return { ...resultFrom(response, body), columnsImplicated: true, refusedColumns };
  }

  return resultFrom(response, body);
};
